import React from 'react'
import { Box, Typography, useTheme } from '@material-ui/core'
import {
  AccountTree as GermlineIcon,
  CompareArrows as SomaticIcon,
  // Search as CarrierIcon,  // Carrier disabled
  Apps as MultipleIcon,
  GroupWork as MultisampleIcon,
} from '@material-ui/icons'
import { motion } from 'framer-motion'
import { OptionCard, pageVariants, cardVariants } from '../styles/StyledComponents'
import { alpha } from '@material-ui/core/styles'

// First wizard step. The four analysis types sit in one row of compact cards
// (icon left, title and a one-line description right) so the step stays short.
// Below lg the cards go two per row, so titles never wrap inside a narrow card.
const StepSelectType = ({ onAnalysisTypeSelect }) => {
  const theme = useTheme()

  const handleGermlineSelect = () => {
    onAnalysisTypeSelect('Germline')
  }

  const handleSomaticSelect = () => {
    onAnalysisTypeSelect('Somatic')
  }

  // const handleCarrierSelect = () => {
  //   onAnalysisTypeSelect('Carrier')
  // }

  const handleMultipleSelect = () => {
    onAnalysisTypeSelect('Multiple')
  }

  const handleMultisampleSelect = () => {
    onAnalysisTypeSelect('Multisample')
  }

  const analysisOptions = [
    {
      id: 'germline',
      title: 'Germline analysis',
      description: 'Inherited variants in a single sample',
      icon: GermlineIcon,
      onClick: handleGermlineSelect,
      disabled: false,
    },
    {
      id: 'somatic',
      title: 'Somatic analysis',
      description: 'Tumor vs normal comparison',
      icon: SomaticIcon,
      onClick: handleSomaticSelect,
      disabled: false,
    },
    {
      id: 'multisample',
      title: 'Multisample analysis',
      description: 'Variants shared across samples',
      icon: MultisampleIcon,
      onClick: handleMultisampleSelect,
      disabled: false,
    },
    {
      id: 'multiple',
      title: 'Cohort creation',
      description: 'A cohort from multiple FASTQ runs',
      icon: MultipleIcon,
      onClick: handleMultipleSelect,
      disabled: false,
    },
  ]

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
      <Box
        role="list"
        aria-label="Analysis type"
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        {analysisOptions.map((option, index) => (
          <motion.div
            key={option.id}
            role="listitem"
            variants={cardVariants}
            custom={index}
            initial="initial"
            animate="animate"
            style={{ minWidth: 0 }}
          >
            <OptionCard
              component="button"
              type="button"
              disabled={option.disabled}
              onClick={!option.disabled ? option.onClick : undefined}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: 88,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                textAlign: 'left',
                font: 'inherit',
                color: 'text.primary',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
                cursor: option.disabled ? 'not-allowed' : 'pointer',
                opacity: option.disabled ? 0.5 : 1,
                transition: theme.transitions.create(['border-color', 'background-color'], {
                  duration: theme.transitions.duration.shorter,
                }),
                ...(!option.disabled && {
                  '&:hover': {
                    borderColor: alpha(theme.palette.text.primary, 0.32),
                    backgroundColor: 'action.hover',
                  },
                }),
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.text.primary}`,
                  outlineOffset: 2,
                },
                // OptionCard spaces its icon for the old stacked layout.
                '& .MuiSvgIcon-root': { mb: 0 },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  color: option.disabled ? 'text.disabled' : 'text.primary',
                }}
              >
                <option.icon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  variant="subtitle2"
                  sx={{
                    display: 'block',
                    fontSize: theme.typography.pxToRem(15),
                    lineHeight: 1.35,
                    color: option.disabled ? 'text.disabled' : 'text.primary',
                  }}
                >
                  {option.title}
                </Typography>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    display: '-webkit-box',
                    mt: 0.25,
                    fontSize: theme.typography.pxToRem(13.5),
                    lineHeight: 1.45,
                    color: option.disabled ? 'text.disabled' : 'text.secondary',
                    overflow: 'hidden',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {option.description}
                </Typography>
              </Box>
            </OptionCard>
          </motion.div>
        ))}
      </Box>
    </motion.div>
  )
}

export default StepSelectType
